import { useState, useEffect } from 'react';
import axios from 'axios';
import { Shield, ChevronRight, Info } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_STYLE = {
  valor:         { bg: 'bg-red-50',     border: 'border-red-200',     text: 'text-red-800',     badge: 'bg-red-100 text-red-700',     accent: 'border-red-400' },
  meritorious:   { bg: 'bg-blue-50',    border: 'border-blue-200',    text: 'text-blue-800',    badge: 'bg-blue-100 text-blue-700',   accent: 'border-blue-400' },
  commendation:  { bg: 'bg-indigo-50',  border: 'border-indigo-200',  text: 'text-indigo-800',  badge: 'bg-indigo-100 text-indigo-700', accent: 'border-indigo-400' },
  achievement:   { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-800', badge: 'bg-emerald-100 text-emerald-700', accent: 'border-emerald-400' },
  unit:          { bg: 'bg-violet-50',  border: 'border-violet-200',  text: 'text-violet-800',  badge: 'bg-violet-100 text-violet-700', accent: 'border-violet-400' },
  conduct:       { bg: 'bg-slate-50',   border: 'border-slate-200',   text: 'text-slate-700',   badge: 'bg-slate-100 text-slate-600',  accent: 'border-slate-400' },
  service:       { bg: 'bg-sky-50',     border: 'border-sky-200',     text: 'text-sky-800',     badge: 'bg-sky-100 text-sky-700',     accent: 'border-sky-400' },
  campaign:      { bg: 'bg-amber-50',   border: 'border-amber-200',   text: 'text-amber-800',   badge: 'bg-amber-100 text-amber-700', accent: 'border-amber-400' },
  badge:         { bg: 'bg-orange-50',  border: 'border-orange-200',  text: 'text-orange-800',  badge: 'bg-orange-100 text-orange-700', accent: 'border-orange-400' },
  tab:           { bg: 'bg-orange-50',  border: 'border-orange-200',  text: 'text-orange-800',  badge: 'bg-orange-100 text-orange-700', accent: 'border-orange-400' },
  qualification: { bg: 'bg-teal-50',    border: 'border-teal-200',    text: 'text-teal-800',    badge: 'bg-teal-100 text-teal-700',   accent: 'border-teal-400' },
  warfare_qualification: { bg: 'bg-teal-50', border: 'border-teal-200', text: 'text-teal-800',   badge: 'bg-teal-100 text-teal-700',   accent: 'border-teal-400' },
  specialty_qualification: { bg: 'bg-teal-50', border: 'border-teal-200', text: 'text-teal-800', badge: 'bg-teal-100 text-teal-700',   accent: 'border-teal-400' },
  military_school: { bg: 'bg-teal-50',  border: 'border-teal-200',    text: 'text-teal-800',    badge: 'bg-teal-100 text-teal-700',   accent: 'border-teal-400' },
};

const CATEGORY_LABELS = {
  valor: 'Valor', meritorious: 'Merit', commendation: 'Commendation',
  achievement: 'Achievement', unit: 'Unit', conduct: 'Conduct',
  service: 'Service', campaign: 'Campaign', badge: 'Badge',
  tab: 'Tab', qualification: 'Qualification', warfare_qualification: 'Warfare Qual',
  specialty_qualification: 'Specialty Qual', military_school: 'School',
};

const DEFAULT_STYLE = { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700', badge: 'bg-slate-100 text-slate-600', accent: 'border-slate-300' };

// Qualification categories vs decoration categories
const QUAL_CATS = new Set(['badge', 'tab', 'qualification', 'warfare_qualification', 'military_school', 'specialty_qualification']);

/**
 * Shared employer-side awards display.
 * Renders structured_awards (with info modal), additional_awards_text, and legacy awards.
 *
 * Props:
 *  - resume: the resume object
 *  - showInfoModal: boolean (default true)
 *  - compact: boolean (default false) — summary card mode: max 2 inline + overflow
 */
export function AwardsDisplay({ resume, showInfoModal = true, compact = false }) {
  const [catalogCache, setCatalogCache] = useState({});
  const [selectedAward, setSelectedAward] = useState(null);

  const hasStructured = resume?.structured_awards?.length > 0;
  const hasLegacy = !!resume?.awards;
  const hasAdditional = !!resume?.additional_awards_text;

  useEffect(() => {
    if (!hasStructured) return;
    let cancelled = false;
    const fetchCatalog = async () => {
      try {
        const resp = await axios.get(`${BACKEND_URL}/api/resume/award-catalog`, {
          params: { q: '', limit: 500 },
          withCredentials: true,
        });
        if (cancelled) return;
        const map = {};
        for (const a of resp.data.awards || []) map[a.award_id] = a;
        setCatalogCache(map);
      } catch { /* graceful fallback */ }
    };
    fetchCatalog();
    return () => { cancelled = true; };
  }, [hasStructured, showInfoModal]);

  if (!hasStructured && !hasLegacy && !hasAdditional) return null;

  const getInfo = (awardId) => catalogCache[awardId];
  const catOf = (sa) => sa.category || catalogCache[sa.award_id]?.category;
  const nameOf = (sa) => catalogCache[sa.award_id]?.display_name || sa.display_name || sa.award_id;

  /* ── COMPACT MODE — qualifications only (summary card signal) ── */
  if (compact) {
    const COMPACT_MAX = 3;
    const quals = (resume.structured_awards || []).filter(sa => QUAL_CATS.has(catOf(sa)));
    if (quals.length === 0) return null; // never clutter the card with decorations

    const visible = quals.slice(0, COMPACT_MAX);
    const overflow = quals.length - COMPACT_MAX;

    return (
      <div data-testid="awards-display-compact">
        <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
          <Shield className="w-3.5 h-3.5" />
          Military Qualifications
        </h4>
        <div className="flex flex-wrap gap-1.5">
          {visible.map((sa, idx) => {
            const info = getInfo(sa.award_id);
            return (
              <button
                key={sa.award_id || idx}
                onClick={() => setSelectedAward(info || { display_name: nameOf(sa), category: catOf(sa) })}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 hover:bg-brand-light border border-slate-200 hover:border-brand/20 rounded-md text-xs font-medium text-slate-700 hover:text-brand transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand/20"
                data-testid={`compact-award-${sa.award_id}`}
              >
                {nameOf(sa)}
                <Info className="w-3 h-3 text-slate-400 flex-shrink-0" />
              </button>
            );
          })}
          {overflow > 0 && (
            <span className="inline-flex items-center px-2 py-0.5 text-xs text-slate-500 font-medium" data-testid="compact-award-overflow">
              +{overflow}
            </span>
          )}
        </div>
        {renderModal()}
      </div>
    );
  }

  /* ── Shared modal renderer ── */
  function renderModal() {
    return (
      <Dialog open={!!selectedAward} onOpenChange={(open) => !open && setSelectedAward(null)}>
        <DialogContent className="sm:max-w-lg p-0 overflow-hidden" data-testid="award-info-modal">
          <VisuallyHidden><DialogTitle>Award Details</DialogTitle></VisuallyHidden>
          {selectedAward && (() => {
            const cat = selectedAward.category;
            const style = CATEGORY_STYLE[cat] || DEFAULT_STYLE;
            return (
              <>
                <div className={`border-l-4 ${style.accent} px-6 pt-6 pb-4`}>
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      {cat && (
                        <span className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full mb-2 ${style.badge}`}>
                          {CATEGORY_LABELS[cat] || cat}
                        </span>
                      )}
                      <h3 className="text-lg font-bold text-slate-900 leading-tight">
                        {selectedAward.display_name}
                      </h3>
                    </div>
                  </div>
                </div>
                <div className="px-6 pb-6 space-y-4">
                  {selectedAward.description && (
                    <div>
                      <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Description</h4>
                      <p className="text-sm text-slate-700 leading-relaxed">{selectedAward.description}</p>
                    </div>
                  )}
                  {selectedAward.employer_translation ? (
                    <div className="bg-brand-light border border-brand/15 rounded-lg p-4">
                      <h4 className="text-[11px] font-bold text-brand uppercase tracking-widest mb-2">What This Signals</h4>
                      <p className="text-sm text-slate-800 leading-relaxed font-medium">{selectedAward.employer_translation}</p>
                    </div>
                  ) : (
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                      <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-2">What This Signals</h4>
                      <p className="text-sm text-slate-600 leading-relaxed">This recognition reflects verified professional merit and performance within the U.S. military.</p>
                    </div>
                  )}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    );
  }

  /* ── FULL MODE — split Qualifications vs Awards ── */
  const structured = resume.structured_awards || [];
  const qualsList = structured.filter(sa => QUAL_CATS.has(catOf(sa)));
  const decosList = structured.filter(sa => !QUAL_CATS.has(catOf(sa)));

  const renderRow = (sa, idx) => {
    const info = getInfo(sa.award_id);
    const name = nameOf(sa);
    const qty = sa.quantity || 1;
    const cat = catOf(sa);
    const style = CATEGORY_STYLE[cat] || DEFAULT_STYLE;
    return (
      <div
        key={sa.award_id || idx}
        data-testid={`employer-award-${sa.award_id}`}
        className={`flex items-center gap-3 px-3.5 py-2.5 rounded-lg border ${style.border} ${style.bg} transition-colors`}
      >
        {cat && (
          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${style.badge} flex-shrink-0`}>
            {CATEGORY_LABELS[cat] || cat}
          </span>
        )}
        <span className={`text-sm font-semibold ${style.text} flex-1 min-w-0`}>
          {name}
          {qty > 1 && <span className="ml-1.5 text-xs font-bold opacity-70">x{qty}</span>}
        </span>
        {showInfoModal && (
          <button
            onClick={() => setSelectedAward(info || { display_name: name, category: cat })}
            className={`flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-md ${style.text} hover:bg-white/60 transition-colors flex-shrink-0`}
            data-testid={`award-info-btn-${sa.award_id}`}
            title="What this signals to employers"
          >
            Details
            <ChevronRight className="w-3 h-3" />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="mb-4" data-testid="awards-display">
      {/* Military Qualifications */}
      {qualsList.length > 0 && (
        <div className="mb-3" data-testid="awards-display-qualifications">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="w-4 h-4 text-brand" />
            <h4 className="text-xs font-bold text-slate-900 uppercase tracking-widest">Military Qualifications</h4>
          </div>
          <div className="space-y-2">{qualsList.map(renderRow)}</div>
        </div>
      )}

      {/* Military Awards & Recognitions */}
      {decosList.length > 0 && (
        <div className="mb-3" data-testid="awards-display-decorations">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="w-4 h-4 text-brand" />
            <h4 className="text-xs font-bold text-slate-900 uppercase tracking-widest">Military Awards</h4>
          </div>
          <div className="space-y-2">{decosList.map(renderRow)}</div>
        </div>
      )}

      {/* Additional free-text (civilian/professional) */}
      {hasAdditional && (
        <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line mb-2 pl-1">
          {resume.additional_awards_text}
        </p>
      )}

      {/* Legacy free-text (only when no structured awards exist) */}
      {hasLegacy && !hasStructured && (
        <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line pl-1">
          {resume.awards}
        </p>
      )}

      {/* Premium info modal (shared) */}
      {renderModal()}
    </div>
  );
}
